import { useState } from 'react';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Badge, Button, Card, EmptyState, ErrorAlert, Field, Modal, PageHeader, Pagination, Spinner } from '../components/ui';
import { CONTENT_GOALS } from '@kontentowo/config';

const STATUS_TONES: Record<string, 'info' | 'success' | 'neutral' | 'danger'> = {
  PLANNED: 'info',
  ACTIVE: 'success',
  PAUSED: 'neutral',
  COMPLETED: 'neutral',
  ARCHIVED: 'neutral',
};

export function CampaignsPage() {
  const [brandId, setBrandId] = useState('');
  const { data: brands } = useAsync(() => api.brands.list(), []);
  const { data, error, loading, reload } = useAsync(() => api.campaigns(brandId || undefined), [brandId]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', goal: 'ACQUISITION', description: '' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [postsCampaign, setPostsCampaign] = useState<{ id: string; name: string } | null>(null);
  const [postsPlatform, setPostsPlatform] = useState('FACEBOOK');
  const [postsCount, setPostsCount] = useState('3');
  const [postsType, setPostsType] = useState('');
  const [postsResult, setPostsResult] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const create = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await api.campaignCreate({
        brandId: brandId || (brands?.[0]?.id ?? ''),
        name: form.name,
        goal: form.goal,
        description: form.description || null,
      });
      setOpen(false);
      setForm({ name: '', goal: 'ACQUISITION', description: '' });
      reload();
    } catch (err) {
      setSaveError(err);
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (id: string, status: string) => {
    setBusyId(id);
    try {
      await api.campaignUpdate(id, { status });
      reload();
    } catch (err) {
      setSaveError(err);
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id: string) => {
    if (!confirm('Usunąć kampanię?')) return;
    setBusyId(id);
    try {
      await api.campaignRemove(id);
      reload();
    } catch (err) {
      setSaveError(err);
    } finally {
      setBusyId(null);
    }
  };

  const generatePosts = async () => {
    if (!postsCampaign) return;
    setBusyId(postsCampaign.id);
    setSaveError(null);
    setPostsResult(null);
    try {
      const result = await api.campaignGeneratePosts(postsCampaign.id, {
        platforms: [postsPlatform],
        count: Number(postsCount),
        postTypes: postsType ? [postsType] : undefined,
      });
      setPostsResult(`Wygenerowano ${result.generated.length} nowych postów. Wszystkie są w statusie DRAFT i czekają w Review Queue.`);
      setPostsCampaign(null);
      reload();
    } catch (err) {
      setSaveError(err);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Campaigns"
        subtitle="Kampanie grupujące treści wokół jednego celu."
        actions={<Button variant="primary" onClick={() => setOpen(true)}>+ Nowa kampania</Button>}
      />
      <Card title="Filtruj">
        <select className="select" value={brandId} onChange={(e) => setBrandId(e.target.value)}>
          <option value="">— wszystkie marki —</option>
          {brands?.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </Card>

      {loading && <Spinner />}
      {error && <ErrorAlert error={error} />}
      <ErrorAlert error={saveError} />
      {postsResult && <Card><div style={{ color: 'var(--success, #16803c)' }}>{postsResult}</div></Card>}
      {data && data.length === 0 && <EmptyState icon="⚑" title="Brak kampanii" hint="Utwórz pierwszą kampanię." />}

      {data && data.length > 0 && (
        <Card flush>
          <table className="table">
            <thead>
              <tr>
                <th>Nazwa</th>
                <th>Brand</th>
                <th>Cel</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Akcje</th>
              </tr>
            </thead>
            <tbody>
              {data.slice((page - 1) * pageSize, page * pageSize).map((c) => (
                <tr key={c.id}>
                  <td style={{ fontWeight: 550 }}>{c.name}</td>
                  <td className="muted">{brands?.find((b) => b.id === c.brandId)?.name ?? c.brandId}</td>
                  <td className="muted">{c.goal ?? '—'}</td>
                  <td>
                    <Badge tone={STATUS_TONES[c.status] ?? 'neutral'}>{c.status}</Badge>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <div className="btn-row" style={{ justifyContent: 'flex-end' }}>
                      <Button size="sm" variant="primary" disabled={busyId === c.id} onClick={() => { setPostsCampaign({ id: c.id, name: c.name }); setPostsResult(null); }}>
                        + Więcej postów
                      </Button>
                      {c.status === 'PLANNED' && (
                        <Button size="sm" variant="primary" disabled={busyId === c.id} onClick={() => setStatus(c.id, 'ACTIVE')}>
                          Aktywuj
                        </Button>
                      )}
                      {c.status === 'ACTIVE' && (
                        <Button size="sm" variant="success" disabled={busyId === c.id} onClick={() => setStatus(c.id, 'COMPLETED')}>
                          Zakończ
                        </Button>
                      )}
                      <Button size="sm" variant="danger" disabled={busyId === c.id} onClick={() => remove(c.id)}>
                        Usuń
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={page} pageSize={pageSize} total={data.length} onChange={setPage} />
        </Card>
      )}

      <Modal
        title="Nowa kampania"
        open={open}
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button onClick={() => setOpen(false)}>Anuluj</Button>
            <Button variant="primary" onClick={create} disabled={saving || !form.name || !brandId && !brands?.length}>
              {saving ? 'Zapisywanie…' : 'Utwórz'}
            </Button>
          </>
        }
      >
        <ErrorAlert error={saveError} />
        <Field label="Marka">
          <select className="select" value={brandId} onChange={(e) => setBrandId(e.target.value)}>
            {brands?.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Nazwa">
          <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="np. Pozyskanie nowych graczy" />
        </Field>
        <div className="form-row">
          <Field label="Cel">
            <select className="select" value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })}>
              {CONTENT_GOALS.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Opis">
            <input className="input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Field>
        </div>
      </Modal>

      <Modal
        title={`Więcej postów: ${postsCampaign?.name ?? ''}`}
        open={Boolean(postsCampaign)}
        onClose={() => setPostsCampaign(null)}
        footer={<>
          <Button onClick={() => setPostsCampaign(null)}>Anuluj</Button>
          <Button variant="primary" onClick={generatePosts} disabled={Boolean(busyId)}>{busyId ? 'Generowanie…' : 'Generuj posty'}</Button>
        </>}
      >
        <p className="muted">Każdy post zostanie zapisany osobno jako DRAFT. Istniejące posty kampanii nie zostaną zmienione.</p>
        <div className="form-row">
          <Field label="Kanał">
            <select className="select" value={postsPlatform} onChange={(e) => setPostsPlatform(e.target.value)}>
              <option value="FACEBOOK">Facebook</option>
              <option value="INSTAGRAM">Instagram</option>
              <option value="TIKTOK">TikTok</option>
              <option value="YOUTUBE">YouTube Shorts</option>
              <option value="X">X</option>
              <option value="LINKEDIN">LinkedIn</option>
              <option value="TELEGRAM">Telegram</option>
            </select>
          </Field>
          <Field label="Liczba postów (1–10)">
            <input className="input" type="number" min="1" max="10" value={postsCount} onChange={(e) => setPostsCount(e.target.value)} />
          </Field>
        </div>
        <Field label="Preferowany typ (opcjonalnie)">
          <select className="select" value={postsType} onChange={(e) => setPostsType(e.target.value)}>
            <option value="">Automatyczny dobór różnorodnych formatów</option>
            <option value="question">Pytanie</option>
            <option value="storytelling">Storytelling</option>
            <option value="scenario">Scenariusz</option>
            <option value="educational">Edukacyjny</option>
            <option value="myth_fact">Mit / fakt</option>
            <option value="checklist">Checklista</option>
            <option value="challenge">Challenge</option>
          </select>
        </Field>
      </Modal>
    </>
  );
}
